<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useResumeRefresh } from "./useResumeRefresh";
import { api, type AccountPayload } from "./api";
import { authClient } from "./auth-client";
import type { TodayPayload } from "./shared";
import LoginView from "./components/LoginView.vue";
import OnboardingView from "./components/OnboardingView.vue";
import PlanView from "./components/PlanView.vue";
import ProgressView from "./components/ProgressView.vue";
import SettingsView from "./components/SettingsView.vue";
import TrainView from "./components/TrainView.vue";

type ViewName = "train" | "plan" | "progress" | "settings";
const authenticated = ref<boolean | null>(null);
const account = ref<AccountPayload | null>(null);
const view = ref<ViewName>((new URLSearchParams(location.search).get("view") as ViewName) || "train");
const today = ref<TodayPayload | null>(null);
const loadingToday = ref(false);
const alert = ref("");
const toast = ref("");
const online = ref(navigator.onLine);
const installPrompt = ref<BeforeInstallPromptEvent | null>(null);
const refreshKey = ref(0);
let todayRequest = 0;
let todayPending = false;
const authStatus = new URLSearchParams(location.search).get("auth");
const authNotice = authStatus === "deleted" ? "Your account has been disabled."
  : authStatus === "password-reset" ? "Password changed. Sign in with the new one."
    : authStatus === "email-changed" ? "Email updated. Sign in again if prompted." : "";

async function checkAuth() {
  try {
    account.value = await api.check();
    authenticated.value = true;
    if (account.value.onboarding.completed) await loadToday();
  } catch { account.value = null; authenticated.value = false; }
}

async function loadToday(background = false) {
  if (background && todayPending) return;
  // Mutation refreshes must supersede older reads, never reuse their stale result.
  const requestId = ++todayRequest;
  todayPending = true;
  loadingToday.value = today.value === null;
  try {
    const next = await api.today();
    if (requestId === todayRequest) today.value = next;
  }
  catch (caught) {
    if (requestId !== todayRequest) return;
    if (caught instanceof Error && "status" in caught && caught.status === 401) authenticated.value = false;
    else showError(caught instanceof Error ? caught.message : "Could not load workout");
  } finally { if (requestId === todayRequest) { loadingToday.value = false; todayPending = false; } }
}

async function signedIn() {
  await checkAuth();
}
async function onboardingCompleted(next: AccountPayload) {
  account.value = next;
  await loadToday();
}
function setView(next: ViewName, refresh = true) {
  view.value = next;
  const url = new URL(location.href); url.searchParams.set("view", next); history.replaceState({}, "", url);
  window.scrollTo({ top: 0, behavior: "smooth" });
  if (refresh && next === "train" && authenticated.value && account.value?.onboarding.completed) void loadToday(true);
}
function showError(message: string) { alert.value = message; window.setTimeout(() => { alert.value = ""; }, 4500); }
function showToast(message: string) { toast.value = message; window.setTimeout(() => { toast.value = ""; }, 3000); }
async function workoutStarted() {
  await loadToday();
  refreshKey.value += 1;
  setView("train", false);
}
async function workoutChanged() { refreshKey.value += 1; await loadToday(); }
async function logout() {
  try { await authClient.signOut(); }
  finally { authenticated.value = false; account.value = null; today.value = null; view.value = "train"; }
}
async function install() { if (installPrompt.value) { await installPrompt.value.prompt(); installPrompt.value = null; } }

useResumeRefresh(
  () => view.value === "train" && Boolean(authenticated.value && account.value?.onboarding.completed),
  () => loadToday(true)
);

onMounted(() => {
  void checkAuth();
  window.addEventListener("online", () => { online.value = true; });
  window.addEventListener("offline", () => { online.value = false; });
  window.addEventListener("beforeinstallprompt", (event) => { event.preventDefault(); installPrompt.value = event as BeforeInstallPromptEvent; });
});
</script>

<template>
  <div v-if="authenticated === null" class="app-loading"><div class="brand-mark"><i></i><span></span><i></i></div><p>Loading your training…</p></div>
  <LoginView v-else-if="!authenticated" :notice="authNotice" @authenticated="signedIn" />
  <OnboardingView v-else-if="account && !account.onboarding.completed" :account="account" @completed="onboardingCompleted" />
  <div v-else-if="account" class="app-shell">
    <aside class="desktop-rail">
      <button class="rail-brand" aria-label="Superset home" @click="setView('train')"><div class="brand-mark"><i></i><span></span><i></i></div><b>SUPERSET</b></button>
      <nav>
        <button :class="{ active: view === 'train' }" @click="setView('train')"><span>↗</span>Train</button>
        <button :class="{ active: view === 'plan' }" @click="setView('plan')"><span>▦</span>Plan</button>
        <button :class="{ active: view === 'progress' }" @click="setView('progress')"><span>⌁</span>Progress</button>
        <button :class="{ active: view === 'settings' }" @click="setView('settings')"><span>⚙</span>Settings</button>
      </nav>
      <div class="rail-footer"><button v-if="installPrompt" @click="install">＋ Install app</button><span><i :class="{ offline: !online }"></i>{{ online ? "Synced" : "Offline" }}</span><button @click="logout">Sign out · {{ account.username }}</button></div>
    </aside>
    <header class="mobile-header"><button class="rail-brand" @click="setView('train')"><div class="brand-mark"><i></i><span></span><i></i></div><b>SUPERSET</b></button><span :class="{ offline: !online }">{{ online ? "SYNCED" : "OFFLINE" }}</span></header>
    <main class="main-content">
      <TrainView v-show="view === 'train'" :payload="today" :loading="loadingToday" @changed="workoutChanged" @error="showError" @toast="showToast" />
      <PlanView v-show="view === 'plan'" :active="view === 'plan'" :refresh-key="refreshKey" @error="showError" @toast="showToast" @workout-started="workoutStarted" />
      <ProgressView v-show="view === 'progress'" :active="view === 'progress'" :refresh-key="refreshKey" @error="showError" />
      <SettingsView v-show="view === 'settings'" :account="account" @saved="account = $event" @error="showError" @toast="showToast" />
    </main>
    <nav class="mobile-nav">
      <button :class="{ active: view === 'train' }" @click="setView('train')"><span>↗</span>TRAIN</button>
      <button :class="{ active: view === 'plan' }" @click="setView('plan')"><span>▦</span>PLAN</button>
      <button :class="{ active: view === 'progress' }" @click="setView('progress')"><span>⌁</span>PROGRESS</button>
      <button :class="{ active: view === 'settings' }" @click="setView('settings')"><span>⚙</span>SETTINGS</button>
    </nav>
    <div v-if="alert" class="app-alert" role="alert"><span>!</span>{{ alert }}<button @click="alert = ''">×</button></div>
    <div v-if="toast" class="app-toast" role="status">✓ {{ toast }}</div>
  </div>
</template>
